import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { openFile } from "@/lib/fileViewer";
import { Search, FileDown, Pencil, Trash2 } from "lucide-react";

const EMPTY_FORM = {
  donor_id: "",
  donor_search: "",
  amount: "",
  payment_date: new Date().toISOString().slice(0, 10),
  method: "bonifico",
  receipt_number: "",
  reference: "",
  campaign: "Erogazione liberale",
  notes: "",
};

export default function NewPaymentDialog({ open, onOpenChange, defaultDonorId = null, onCreated, payment = null }) {
  const qc = useQueryClient();
  const isEdit = !!payment;
  const [form, setForm] = useState(EMPTY_FORM);

  // Reset/precompila all'apertura
  useEffect(() => {
    if (!open) return;
    if (isEdit && payment) {
      setForm({
        donor_id: payment.donor_id || "",
        donor_search: payment.donor_name || "",
        amount: String(payment.amount ?? ""),
        payment_date: payment.payment_date || new Date().toISOString().slice(0, 10),
        method: payment.method || "bonifico",
        receipt_number: payment.receipt_number || "",
        reference: payment.reference || "",
        campaign: payment.campaign || "Erogazione liberale",
        notes: payment.notes || "",
      });
    } else {
      setForm({ ...EMPTY_FORM, donor_id: defaultDonorId || "" });
    }
  }, [open, isEdit, payment, defaultDonorId]);

  // suggerimento numero ricevuta (solo in create)
  const year = (form.payment_date || "").slice(0, 4);
  const { data: nextData } = useQuery({
    queryKey: ["next-receipt", year],
    queryFn: async () => (await api.get(`/payments/next-receipt-number?year=${year}`)).data,
    enabled: !!year && open && !isEdit,
  });
  useEffect(() => {
    if (open && !isEdit && nextData?.next && !form.receipt_number) {
      setForm((f) => ({ ...f, receipt_number: nextData.next }));
    }
  }, [nextData, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // ricerca donatori (solo se non è edit e non c'è un donatore preselezionato)
  const { data: donorsResult } = useQuery({
    queryKey: ["donors-picker", form.donor_search],
    queryFn: async () => (await api.get("/donors", { params: { paginated: true, limit: 20, search: form.donor_search || undefined } })).data,
    enabled: !defaultDonorId && !isEdit && open,
  });
  const donors = donorsResult?.items || [];
  const selectedDonor = donors.find((d) => d.id === form.donor_id);

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        donor_id: form.donor_id,
        amount: parseFloat(form.amount),
        payment_date: form.payment_date,
        method: form.method,
        receipt_number: form.receipt_number || null,
        reference: form.reference || null,
        campaign: form.campaign || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        return (await api.patch(`/payments/${payment.id}`, body)).data;
      }
      return (await api.post("/payments", body)).data;
    },
    onSuccess: (data) => {
      toast.success(isEdit ? "Donazione aggiornata" : "Donazione registrata");
      qc.invalidateQueries({ queryKey: ["payments-page"] });
      qc.invalidateQueries({ queryKey: ["payments-kpi"] });
      qc.invalidateQueries({ queryKey: ["donor"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["next-receipt"] });
      onOpenChange(false);
      if (!isEdit) setForm((f) => ({ ...f, amount: "", receipt_number: "", reference: "", notes: "" }));
      onCreated?.(data);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const canSubmit = form.donor_id && parseFloat(form.amount) > 0 && form.payment_date;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica donazione" : "Nuova donazione"}</DialogTitle>
          {isEdit && <DialogDescription className="text-xs">Aggiorna importo, data, metodo o numero ricevuta.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4">
          {isEdit ? (
            <div>
              <Label>Donatore</Label>
              <div className="mt-1.5 text-sm px-3 py-2 bg-stone-50 border border-stone-200 rounded-md text-stone-700">
                {form.donor_search || "—"}
              </div>
            </div>
          ) : !defaultDonorId && (
            <div>
              <Label>Donatore</Label>
              <div className="relative mt-1.5">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                <Input
                  data-testid="new-pay-donor-search"
                  value={form.donor_search}
                  onChange={(e) => setForm({ ...form, donor_search: e.target.value, donor_id: "" })}
                  placeholder="Cerca donatore (nome, CF, referente)"
                  className="pl-9"
                />
              </div>
              {form.donor_search && donors.length > 0 && !selectedDonor && (
                <div className="mt-1.5 border border-stone-200 rounded-md max-h-40 overflow-y-auto bg-white">
                  {donors.map((d) => {
                    const name = d.org_name ? (d.referente ? `${d.org_name} — ${d.referente}` : d.org_name) : `${d.first_name || ""} ${d.last_name || ""}`.trim();
                    return (
                      <button
                        key={d.id}
                        type="button"
                        data-testid={`new-pay-donor-${d.id}`}
                        onClick={() => setForm({ ...form, donor_id: d.id, donor_search: name })}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-stone-50"
                      >
                        {name}{d.cf ? ` · ${d.cf}` : ""}
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedDonor && (
                <div className="mt-1.5 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                  ✓ {form.donor_search}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data pagamento</Label>
              <Input
                data-testid="new-pay-date"
                type="date"
                value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label>Importo (€)</Label>
              <Input
                data-testid="new-pay-amount"
                type="number" step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="mt-1.5"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Metodo</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="new-pay-method" className="mt-1.5"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bonifico">Bonifico</SelectItem>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="stripe">Stripe</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>N° ricevuta {!isEdit && nextData?.next && <span className="text-[10px] text-stone-500">(suggerito: {nextData.next})</span>}</Label>
              <Input
                data-testid="new-pay-receipt-number"
                value={form.receipt_number}
                onChange={(e) => setForm({ ...form, receipt_number: e.target.value })}
                placeholder="0001/D/2026"
                className="mt-1.5 mono"
              />
            </div>
          </div>

          <div>
            <Label>Causale</Label>
            <Input
              data-testid="new-pay-campaign"
              value={form.campaign}
              onChange={(e) => setForm({ ...form, campaign: e.target.value })}
              className="mt-1.5"
              placeholder="Erogazione liberale"
            />
          </div>

          <div>
            <Label>Riferimento bancario / CRO (opzionale)</Label>
            <Input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="mt-1.5 mono"
              placeholder="Es. CRO 123456789"
            />
          </div>

          <div>
            <Label>Note</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" rows={2}/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            data-testid="new-pay-save"
            className="bg-[#1C352D] hover:bg-[#264A3E]"
            disabled={!canSubmit || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? "Salvataggio…" : (isEdit ? "Salva modifiche" : "Registra donazione")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReceiptButton({ payment }) {
  const openReceipt = async () => {
    try {
      const res = await api.get(`/payments/${payment.id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      openFile(url, "Ricevuta.pdf");
    } catch (e) {
      toast.error("Errore generazione ricevuta");
    }
  };
  return (
    <Button
      variant="ghost"
      size="icon"
      data-testid={`receipt-${payment.id}`}
      onClick={openReceipt}
      className="h-8 w-8 rounded-full hover:bg-stone-100"
      title="Ricevuta PDF"
    >
      <FileDown size={14}/>
    </Button>
  );
}

export function EditPaymentButton({ payment }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        data-testid={`edit-payment-${payment.id}`}
        onClick={() => setOpen(true)}
        className="h-8 w-8 rounded-full hover:bg-stone-100"
        title="Modifica donazione"
      >
        <Pencil size={14}/>
      </Button>
      {open && <NewPaymentDialog open={open} onOpenChange={setOpen} payment={payment}/>}
    </>
  );
}

export function DeletePaymentButton({ payment }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const delMut = useMutation({
    mutationFn: async () => (await api.delete(`/payments/${payment.id}`)).data,
    onSuccess: () => {
      toast.success("Donazione eliminata");
      qc.invalidateQueries({ queryKey: ["payments-page"] });
      qc.invalidateQueries({ queryKey: ["payments-kpi"] });
      qc.invalidateQueries({ queryKey: ["donor"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setConfirming(false);
    },
    onError: (e) => {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
      setConfirming(false);
    },
  });

  return (
    <Dialog open={confirming} onOpenChange={setConfirming}>
      <Button
        variant="ghost"
        size="icon"
        data-testid={`delete-payment-${payment.id}`}
        onClick={() => setConfirming(true)}
        className="h-8 w-8 rounded-full hover:bg-red-50 text-red-600 hover:text-red-700"
        title="Elimina donazione"
      >
        <Trash2 size={14}/>
      </Button>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Eliminare questa donazione?</DialogTitle>
          <DialogDescription className="text-sm text-stone-600 pt-2">
            <div><b>Donatore:</b> {payment.donor_name}</div>
            <div><b>Data:</b> {payment.payment_date}</div>
            <div><b>Importo:</b> € {Number(payment.amount || 0).toFixed(2)}</div>
            {payment.receipt_number && <div><b>Ricevuta:</b> {payment.receipt_number}</div>}
            <div className="mt-3 text-red-700">L'azione è irreversibile.</div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirming(false)}>Annulla</Button>
          <Button
            data-testid={`confirm-delete-payment-${payment.id}`}
            className="bg-red-600 hover:bg-red-700 text-white"
            disabled={delMut.isPending}
            onClick={() => delMut.mutate()}
          >
            {delMut.isPending ? "Eliminazione…" : "Elimina definitivamente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
